import { FC, ReactElement, useMemo } from "react";
import { Box, Card, CircularProgress, Stack, Typography } from "@mui/material";
import ReactApexChart from "react-apexcharts";

import { barChartConfig } from "@/config";
import { formatNumberWithSuffix } from "@/utils";
import { responsiveFontSizes, spacing } from "@/utils/responsive";

interface IQuater { 1: number; 2: number; 3: number; 4: number; }

const ImportCard: FC<{
  data?: ImportsValueReponse["dashboardVisibilityOverallImportValue"]["data"];
  xAxisMode: "quarter" | "year";
  isLoading: boolean;
}> = ({ data = [], xAxisMode, isLoading }): ReactElement => {

  // no toggle anymore

  const { quarters, dutySavingsByQuarter, years, dutySavings } = useMemo(() => {
    const quarters: string[][] = [];
    const dutySavingsByQuarter: number[] = [];
    const safeData = data ?? [];
    const dutySavings: number[] = [...safeData.map(({ totalGoodsValue }) => totalGoodsValue)];
    const dutySavingsWithEmptyEdges: number[] = [0, ...safeData.map(({ totalGoodsValue }) => totalGoodsValue), 0];

    const years = safeData.map(({ year }) => year.toString());
    const yearsWithEmptyEdges: string[] = ["0", ...years, "0"];

    const groupedData = safeData.reduce((acc: { [key: number]: IQuater }, { year, month, totalGoodsValue }) => {
      if (!acc[year]) acc[year] = { 1: 0, 2: 0, 3: 0, 4: 0 };
      const quarter = Math.ceil(month / 3);
      acc[year][quarter as keyof IQuater] += totalGoodsValue;
      return acc;
    }, {} as { [key: number]: IQuater });

    Object.keys(groupedData).forEach((year) => {
      Object.keys(groupedData[Number(year)]).forEach((quarter) => {
        const totalDutySavings = groupedData[Number(year)][quarter as unknown as keyof IQuater];
        if (totalDutySavings > 0) {
          quarters.push([year, `(Q${quarter})`]);
          dutySavingsByQuarter.push(totalDutySavings);
        }
      });
    });

    return {
      quarters,
      dutySavingsByQuarter,
      years: years.length === 1 ? yearsWithEmptyEdges : years,
      dutySavings: years.length === 1 ? dutySavingsWithEmptyEdges : dutySavings,
    };
  }, [data]);

  const total = useMemo(() => (data ?? []).reduce((prev, curr) => prev + curr.totalGoodsValue, 0), [data]);

  return (
    <Card sx={{ p: { xs: 1, md: 1.25 }, height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1 }}>
          <Typography color="neutral.500" variant="body1" fontWeight={500} mb={spacing.xs}>
            Overall Import Value
          </Typography>
          <Typography color="neutral.1000" fontFamily="Plus Jakarta Sans" variant="h2" fontWeight={700} sx={{ fontSize: responsiveFontSizes["3xl"], lineHeight: 1.1 }}>
            ${formatNumberWithSuffix(total)}
          </Typography>
        </Box>
        {/* Toggle removed per new behavior */}
      </Stack>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {isLoading ? (
          <Stack height="100%" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Stack>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ReactApexChart
              options={{ ...barChartConfig, xaxis: { ...barChartConfig.xaxis, categories: xAxisMode === "quarter" ? quarters : years } }}
              series={[{ name: xAxisMode === "quarter" ? "Quarterly Data" : "Yearly Data", data: xAxisMode === "quarter" ? dutySavingsByQuarter : dutySavings }]}
              type="area"
              height="100%"
            />
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default ImportCard;
